import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, PlayCircle, Award } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
    if (user) api.get("/courses/progress/me").then((r) => {
      const m = {};
      (r.data.progress || []).forEach((p) => { if (p.course_id === id) m[p.lesson_id] = p.completed; });
      setProgress(m);
    });
  }, [id, user]);

  const toggle = async (lesson_id) => {
    if (!user) { toast.error("Sign in to track progress."); return; }
    const next = !progress[lesson_id];
    setProgress({ ...progress, [lesson_id]: next });
    try { await api.post("/courses/progress", { course_id: id, lesson_id, completed: next }); }
    catch { toast.error("Couldn't save progress."); }
  };

  if (c === false) return <Section className="pt-40 pb-40"><Container><DisplayH1>Course not found.</DisplayH1></Container></Section>;
  if (!c) return <Section className="pt-40 pb-40"><Container><div className="text-[color:var(--wk-bone-dim)]">Loading…</div></Container></Section>;

  const done = Object.values(progress).filter(Boolean).length;
  const total = c.lessons.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <>
      <Section testid="course-hero" className="pt-32 pb-16">
        <Container>
          <Link to="/university" className="link-underline text-sm text-[color:var(--wk-bone-dim)]">← Back to the library</Link>
          <div className="mt-6 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Eyebrow>{c.level} · {c.duration}</Eyebrow>
              <DisplayH1 className="mt-4">{c.title}</DisplayH1>
              <p className="mt-6 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">{c.description}</p>
              <div className="mt-6 text-sm">Taught by <span className="text-[color:var(--wk-gold)]">{c.instructor}</span></div>
              {user && (
                <div className="mt-8">
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden max-w-md">
                    <div className="h-full bg-[color:var(--wk-gold)]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">{done} of {total} lessons complete · {pct}%</div>
                  {done === total && total > 0 && (
                    <a
                      href={`${BACKEND_URL}/api/certificates/${c.course_id}`}
                      className="btn-gold mt-6 inline-flex"
                      data-testid="course-download-cert"
                    >
                      <Award size={16} /> Download signed certificate
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                <img src={c.image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 cinema-overlay" />
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section testid="course-lessons" className="pb-28">
        <Container>
          <Eyebrow>Lessons</Eyebrow>
          <div className="mt-6 card-lux divide-y divide-white/5">
            {c.lessons.map((l, i) => {
              const isDone = progress[l.lesson_id];
              return (
                <button
                  key={l.lesson_id}
                  data-testid={`course-lesson-${l.lesson_id}`}
                  onClick={() => toggle(l.lesson_id)}
                  className="w-full text-left flex items-center gap-6 p-5 hover:bg-white/[0.02] transition-colors"
                >
                  {isDone ? <CheckCircle2 className="text-[color:var(--wk-gold)]" /> : <PlayCircle className="text-white/60" />}
                  <div className="font-serif-display text-2xl text-[color:var(--wk-bone-dim)] w-12">{String(i + 1).padStart(2, "0")}</div>
                  <div className="flex-1">
                    <div className="font-serif-display text-xl">{l.title}</div>
                    <div className="text-xs text-[color:var(--wk-bone-dim)]">{l.duration}</div>
                  </div>
                  <div className={`overline text-[10px] ${isDone ? "text-[color:var(--wk-gold)]" : "text-[color:var(--wk-bone-dim)]"}`}>
                    {isDone ? "Completed" : "Mark done"}
                  </div>
                </button>
              );
            })}
          </div>
        </Container>
      </Section>
    </>
  );
}
